"use client";

import { useCallback, useEffect, useState } from "react";

interface UseScrollVisibilityOptions {
  threshold?: number;
  throttleTime?: number;
}

/**
 * Hook for controlling element visibility based on scroll position
 * @param options.threshold Number of pixels for when to start hiding the element
 * @param options.throttleTime Time in milliseconds to throttle scroll events
 */
export function useScrollVisibility(options: UseScrollVisibilityOptions = {}) {
  const { threshold = 100, throttleTime = 100 } = options;

  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const controlVisibility = useCallback(() => {
    const currentScrollY = window.scrollY;
    const minScrollDifference = 30;
    const isScrollingUp = currentScrollY < lastScrollY;
    const isScrollingDown = currentScrollY > lastScrollY;
    const scrollDistance = Math.abs(currentScrollY - lastScrollY);
    const isAboveThreshold = currentScrollY < threshold;
    const isBelowThreshold = currentScrollY > threshold;
    const isSignificantScroll = scrollDistance > minScrollDifference;

    // Show element when significantly scrolling up or near the top
    if ((isScrollingUp && isSignificantScroll) || isAboveThreshold) {
      setIsVisible(true);
    }
    // Hide element when significantly scrolling down and not at the top
    else if (isScrollingDown && isSignificantScroll && isBelowThreshold) {
      setIsVisible(false);
    }

    // Update scroll position
    setLastScrollY(currentScrollY);
  }, [lastScrollY, threshold]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    const throttledScrollHandler = () => {
      if (!timeoutId) {
        timeoutId = setTimeout(() => {
          controlVisibility();
          timeoutId = null;
        }, throttleTime);
      }
    };

    window.addEventListener("scroll", throttledScrollHandler);

    return () => {
      window.removeEventListener("scroll", throttledScrollHandler);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [controlVisibility, throttleTime]);

  return { isVisible, lastScrollY };
}
